import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Heart, Star } from "lucide-react";

const Icoane = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-burgundy-dark/10 via-gold/5 to-background -z-10" />
        <div className="absolute top-20 right-10 w-96 h-96 bg-gold/10 rounded-full blur-3xl -z-10 animate-float" />
        <div className="absolute bottom-10 left-10 w-[500px] h-[500px] bg-primary/10 rounded-full blur-3xl -z-10 animate-float" style={{ animationDelay: '1s' }} />
        
        <div className="container mx-auto max-w-5xl text-center">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold/10 backdrop-blur-sm border border-gold/20 text-gold mb-8 animate-fade-in">
            <Sparkles className="w-5 h-5" />
            <span className="text-sm font-semibold font-lora">Artă sacră autentică</span>
          </div>
          
          <h1 className="text-6xl md:text-8xl font-bold mb-8 bg-gradient-to-r from-burgundy-dark via-gold to-primary bg-clip-text text-transparent animate-fade-in leading-tight font-playfair">
            Icoane Pictate
          </h1>
          
          <p className="text-xl md:text-3xl text-muted-foreground mb-8 leading-relaxed animate-fade-in font-lora max-w-4xl mx-auto" style={{ animationDelay: '0.2s' }}>
            Fiecare icoană este o fereastră către divin, pictată cu răbdare și credință după canoanele tradiționale ale artei bizantine
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 px-4 bg-gradient-to-b from-background to-cream/20">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-2 gap-20 items-center mb-24">
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 rounded-full border border-gold/20">
                <Star className="w-5 h-5 fill-gold text-gold" />
                <span className="font-semibold text-gold font-lora">Tradiție și măiestrie</span>
              </div>
              
              <h2 className="text-5xl md:text-6xl font-bold leading-tight text-primary font-playfair">
                Icoane pictate după tehnica bizantină tradițională
              </h2>
              
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-lora">
                Icoanele noastre sunt realizate manual, respectând cu sfințenie tehnicile vechi de secole ale picturii bizantine. Fiecare lucrare este o rugăciune materializată, un dar din har pentru cei care caută frumusețea spirituală autentică.
              </p>
              
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-lora">
                Folosim materiale naturale de înaltă calitate: lemn masiv pregătit în straturi, pigmenți minerali nobili, foiță de aur de 22-24k, și vopsele pe bază de ou. Fiecare icoană este binecuvântată și poate fi personalizată conform dorinței dumneavoastră.
              </p>
            </div>

            <div className="relative group animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="absolute -inset-6 bg-gradient-to-r from-gold/20 via-primary/20 to-burgundy/20 rounded-3xl blur-3xl group-hover:blur-[60px] transition-all duration-700 animate-float" />
              <div className="relative bg-card/90 backdrop-blur-sm rounded-3xl p-10 shadow-2xl border-2 border-border/50 hover:border-gold/30 transition-all duration-500">
                <div className="aspect-[3/4] bg-gradient-to-br from-gold/10 via-primary/10 to-burgundy/10 rounded-2xl flex items-center justify-center overflow-hidden relative group-hover:scale-[1.02] transition-transform duration-500">
                  <Heart className="w-32 h-32 text-gold/40 group-hover:text-gold/60 transition-colors duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-burgundy-dark/20 to-transparent" />
                </div>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-10 mb-24">
            {[
              {
                title: "Pictură tradițională",
                description: "Tehnica bizantină autentică transmisă de-a lungul secolelor",
                icon: "🎨",
                gradient: "from-burgundy/10 to-primary/10"
              },
              {
                title: "Materiale premium",
                description: "Lemn masiv, foiță de aur, pigmenți naturali de cea mai înaltă calitate",
                icon: "✨",
                gradient: "from-gold/10 to-accent/10"
              },
              {
                title: "Lucrări personalizate",
                description: "Fiecare icoană poate fi realizată după dimensiuni și sfințenie specifică",
                icon: "🙏",
                gradient: "from-primary/10 to-burgundy/10"
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className={`group p-10 rounded-3xl bg-gradient-to-br ${feature.gradient} backdrop-blur-sm border-2 border-border/50 hover:border-gold/40 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 animate-fade-in relative overflow-hidden`}
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gold/5 rounded-full blur-2xl group-hover:bg-gold/10 transition-colors duration-500" />
                <div className="relative">
                  <div className="text-7xl mb-6 group-hover:scale-110 transition-transform duration-500">{feature.icon}</div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-4 group-hover:text-primary transition-colors font-playfair">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-lg font-lora">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="relative rounded-[2rem] overflow-hidden p-16 md:p-24 text-center animate-fade-in bg-gradient-to-br from-burgundy-dark/10 via-gold/5 to-primary/10 backdrop-blur-sm border-2 border-gold/20">
            <div className="absolute inset-0 bg-gradient-to-br from-gold/20 via-primary/10 to-burgundy/20 opacity-50 -z-10" />
            <div className="absolute top-10 right-10 w-64 h-64 bg-gold/20 rounded-full blur-3xl animate-float" />
            <div className="absolute bottom-10 left-10 w-64 h-64 bg-primary/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
            
            <h2 className="text-5xl md:text-6xl font-bold mb-8 text-primary font-playfair relative">
              Descoperiți colecția noastră de icoane
            </h2>
            <p className="text-xl md:text-2xl text-muted-foreground mb-10 max-w-3xl mx-auto font-lora leading-relaxed">
              Explorați icoanele pictate manual și găsiți cadoul spiritual perfect pentru persoanele dragi
            </p>
            
            <div className="flex flex-wrap gap-6 justify-center">
              <Link to="/magazin">
                <Button size="lg" className="text-lg px-10 py-7 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-500 group bg-primary hover:bg-primary/90 hover-scale hover-glow">
                  Vezi Magazinul
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              
              <Link to="/#contact">
                <Button size="lg" variant="outline" className="text-lg px-10 py-7 rounded-full shadow-xl hover:shadow-2xl transition-all duration-500 bg-cream/10 hover:bg-cream/20 backdrop-blur-md border-2 border-primary/30 hover:border-gold/50">
                  Comandă personalizată
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Icoane;
